import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ShopifyProduct, ShopifyVariant, ShopifyImage } from '../../types/shopify';
import ShopifyService from '../../services/shopify';
import './ProductPage.scss';

interface ProductPageProps {
  onAddToCart: (variantId: string, quantity: number) => Promise<void>;
}

const ProductPage: React.FC<ProductPageProps> = ({ onAddToCart }) => {
  const { handle } = useParams<{ handle: string }>();
  const [product, setProduct] = useState<ShopifyProduct | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ShopifyVariant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBaseColor, setSelectedBaseColor] = useState<string | null>(null);
  const [selectedPrint, setSelectedPrint] = useState<string | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Нормализовать название цвета (Gray/Grey -> Grey)
  const normalizeColorName = (color: string): string => {
    if (!color) return '';
    const normalized = color.toLowerCase().trim();
    if (normalized === 'gray' || normalized === 'grey') {
      return 'Grey';
    }
    // Правильная нормализация: первая буква заглавная, остальные строчные
    return color.charAt(0).toUpperCase() + color.slice(1).toLowerCase();
  };

  // Извлечь основной цвет из варианта
  const extractBaseColor = (variant: ShopifyVariant): string | null => {
    // Сначала проверяем опцию Color (если она есть)
    const colorOption = variant.selectedOptions.find(opt => 
      opt.name.toLowerCase() === 'color'
    );
    if (colorOption) {
      // Нормализуем название цвета (Gray -> Grey для единообразия)
      return normalizeColorName(colorOption.value);
    }
    
    // Если нет опции Color, пытаемся извлечь из названия варианта
    const variantTitle = variant.title.toLowerCase();
    const colorWords = ['black', 'white', 'yellow', 'grey', 'gray', 'red', 'blue', 'green'];
    for (const color of colorWords) {
      if (variantTitle.includes(color)) {
        return normalizeColorName(color);
      }
    }
    
    // Если ничего не найдено, берем первое слово из названия
    const firstWord = variant.title.split(/[\s\/\+\-]+/)[0];
    return firstWord ? normalizeColorName(firstWord) : null;
  };

  // Извлечь принт из варианта
  const extractPrint = (variant: ShopifyVariant): string | null => {
    // Сначала проверяем опцию Print (если она есть)
    const printOption = variant.selectedOptions.find(opt => 
      opt.name.toLowerCase() === 'print'
    );
    if (printOption) {
      // Возвращаем значение опции Print как есть
      return printOption.value;
    }
    
    // Если нет опции Print, ищем другие опции (кроме Color и Size)
    const otherOptions = variant.selectedOptions
      .filter(opt => {
        const optName = opt.name.toLowerCase();
        return optName !== 'color' && optName !== 'size';
      })
      .map(opt => opt.value);
    
    if (otherOptions.length > 0) {
      return otherOptions.join(' ');
    }
    
    // Если нет опций, пытаемся извлечь из названия варианта
    const variantTitle = variant.title.toLowerCase();
    const printWords = variantTitle.split(/[\s\/\+\-]+/).slice(1); // Пропускаем первый цвет
    if (printWords.length > 0) {
      const printText = printWords.join(' ').trim();
      return printText ? printText.charAt(0).toUpperCase() + printText.slice(1) : null;
    }
    
    return null;
  };

  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;

      try {
        setLoading(true);
        setError(null);
        
        const productData = await ShopifyService.getProduct(handle);
        
        if (productData) {
          setProduct(productData);
          // Set first available variant as default
          const firstAvailableVariant = productData.variants.find(v => v.availableForSale);
          const defaultVariant = firstAvailableVariant || productData.variants[0];
          setSelectedVariant(defaultVariant);
          
          // Set initial base color and print from default variant
          if (defaultVariant) {
            const baseColor = extractBaseColor(defaultVariant);
            const print = extractPrint(defaultVariant);
            if (baseColor) setSelectedBaseColor(baseColor);
            if (print) setSelectedPrint(print);
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Failed to load product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [handle]);


  // Update variant when color and print are selected
  useEffect(() => {
    if (!product || !selectedBaseColor) return;
    
    // Check if current variant already matches selected color and print
    if (selectedVariant) {
      const currentBaseColor = extractBaseColor(selectedVariant);
      const currentPrint = extractPrint(selectedVariant);
      const colorMatch = currentBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase();
      const printMatch = !selectedPrint || currentPrint?.toLowerCase() === selectedPrint.toLowerCase();
      
      // If current variant matches, don't update
      if (colorMatch && printMatch) {
        return;
      }
    }
    
    // Find variant matching selected color and print
    // Сначала ищем доступный вариант
    let matchingVariant = product.variants.find(v => {
      const variantBaseColor = extractBaseColor(v);
      const variantPrint = extractPrint(v);
      
      const colorMatch = variantBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase();
      const printMatch = !selectedPrint || variantPrint?.toLowerCase() === selectedPrint.toLowerCase();
      
      return colorMatch && printMatch && v.availableForSale;
    });
    
    // Если нет доступного, ищем любой вариант (для отображения "Sold Out")
    if (!matchingVariant) {
      matchingVariant = product.variants.find(v => {
        const variantBaseColor = extractBaseColor(v);
        const variantPrint = extractPrint(v);
        
        const colorMatch = variantBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase();
        const printMatch = !selectedPrint || variantPrint?.toLowerCase() === selectedPrint.toLowerCase();
        
        return colorMatch && printMatch;
      });
    }
    
    // Only update if we found a different variant
    if (matchingVariant && matchingVariant.id !== selectedVariant?.id) {
      setSelectedVariant(matchingVariant);
    }
  }, [selectedBaseColor, selectedPrint, product]);


  // Получить все принты для выбранного основного цвета (только из вариантов)
  const getPrintsForColor = useCallback((baseColor: string): string[] => {
    if (!product || !baseColor) return [];
    
    const colorLower = baseColor.toLowerCase();
    const prints = new Set<string>();
    
    // Получаем принты только из вариантов Shopify
    product.variants.forEach(variant => {
      const variantBaseColor = extractBaseColor(variant);
      if (variantBaseColor?.toLowerCase() === colorLower) {
        const print = extractPrint(variant);
        if (print) {
          prints.add(print);
        }
      }
    });
    
    return Array.from(prints).sort();
  }, [product]);

  const handleVariantChange = (variant: ShopifyVariant) => {
    setSelectedVariant(variant);
    // Update base color and print from variant
    const baseColor = extractBaseColor(variant);
    const print = extractPrint(variant);
    if (baseColor) setSelectedBaseColor(baseColor);
    if (print) setSelectedPrint(print);
  };

  const handleBaseColorSelect = (color: string) => {
    setSelectedBaseColor(color);
    
    // Автоматически выбираем первый доступный принт для выбранного цвета
    const printsForColor = getPrintsForColor(color);
    if (printsForColor.length > 0) {
      // Выбираем первый принт из списка
      setSelectedPrint(printsForColor[0]);
    } else {
      // Если принтов нет, сбрасываем выбор принта
      setSelectedPrint(null);
    }
  };

  const handlePrintSelect = (print: string) => {
    setSelectedPrint(print);
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      console.warn('No variant selected');
      return;
    }

    if (!selectedVariant.availableForSale) {
      console.warn('Variant is not available for sale');
      return;
    }

    if (isAddingToCart) {
      return; // Prevent multiple clicks
    }

    try {
      setIsAddingToCart(true);
      console.log('Adding to cart:', { variantId: selectedVariant.id, quantity: 1 });
      await onAddToCart(selectedVariant.id, 1);
    } catch (error) {
      console.error('Error in handleAddToCart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(parseFloat(amount));
  };

  // Extract brand from product title or tags
  const getBrand = (): string => {
    if (!product) return '';
    
    // Try to find brand in tags first
    const brandTag = product.tags.find(tag => 
      ['brand', 'designer', 'vendor'].some(keyword => tag.toLowerCase().includes(keyword))
    );
    if (brandTag) {
      return brandTag.replace(/^(brand|designer|vendor):?\s*/i, '');
    }
    
    // Extract from title (first word or words before dash/slash)
    const titleParts = product.title.split(/[\s\-/]+/);
    if (titleParts.length > 1) {
      return titleParts[0];
    }
    
    return '';
  };

  // Extract product name (without brand)
  const getProductName = (): string => {
    if (!product) return '';
    
    const brand = getBrand();
    if (brand) {
      return product.title.replace(new RegExp(`^${brand}\\s*[-/]?\\s*`, 'i'), '').trim();
    }
    return product.title;
  };

  const getUniqueOptions = (optionName: string) => {
    if (!product) return [];
    
    const options = product.variants
      .map(variant => variant.selectedOptions.find(opt => opt.name === optionName)?.value)
      .filter((value, index, array) => value && array.indexOf(value) === index);
    
    return options as string[];
  };

  // Получить все уникальные основные цвета (только из вариантов)
  const baseColors = useMemo(() => {
    if (!product) return [];
    
    const colors = new Set<string>();
    
    // Получаем цвета только из вариантов Shopify
    product.variants.forEach(variant => {
      const baseColor = extractBaseColor(variant);
      if (baseColor) {
        colors.add(baseColor);
      }
    });
    
    return Array.from(colors).sort();
  }, [product]);

  // Получить уникальные комбинации цветов (например, "Black + White Print", "Black + Yellow Print")
  const getUniqueColorCombinations = () => {
    if (!product) return [];
    
    // Если есть опция Color, используем её значения
    const colorOptions = getUniqueOptions('Color');
    if (colorOptions.length > 0) {
      return colorOptions;
    }
    
    // Если нет опции Color, создаем комбинации из названий вариантов
    // Группируем варианты по уникальным комбинациям опций (кроме Size)
    const combinations = new Set<string>();
    
    product.variants.forEach(variant => {
      // Собираем все опции кроме Size
      const nonSizeOptions = variant.selectedOptions
        .filter(opt => opt.name !== 'Size')
        .map(opt => opt.value)
        .sort()
        .join(' + ');
      
      if (nonSizeOptions) {
        combinations.add(nonSizeOptions);
      } else {
        // Если нет опций, используем название варианта
        combinations.add(variant.title);
      }
    });
    
    return Array.from(combinations);
  };

  // Получить изображения для выбранного цвета и принта (мемоизировано)
  const colorImages = useMemo(() => {
    if (!product) return [];
    
    // Исключаем превью-изображения
    const nonPreviewImages = product.images.filter(img => {
      const altText = (img.altText || '').toLowerCase();
      return !altText.includes('preview') && 
             !altText.includes('hero') && 
             !altText.includes('model');
    });
    
    // Вспомогательная функция для получения fallback изображения из варианта
    const getVariantImage = (): ShopifyImage[] => {
      if (selectedVariant?.image) {
        return [selectedVariant.image];
      }
      // Если у варианта нет изображения, берём первое non-preview
      return nonPreviewImages.length > 0 ? [nonPreviewImages[0]] : product.images.slice(0, 1);
    };
    
    if (nonPreviewImages.length === 0) {
      return getVariantImage();
    }
    
    const colorWords = ['black', 'white', 'yellow', 'grey', 'gray', 'red', 'blue', 'green'];
    
    // Если выбран цвет и принт, ищем по комбинации в alt text
    if (selectedBaseColor && selectedPrint) {
      const colorLower = selectedBaseColor.toLowerCase();
      // Нормализуем для поиска (Gray -> grey)
      const normalizedColor = colorLower === 'gray' ? 'grey' : colorLower;
      
      // Извлекаем цвет принта из selectedPrint (например, "White Print" -> "white")
      const printColorMatch = selectedPrint.toLowerCase().match(/(black|white|yellow|grey|gray|red|blue|green)/);
      const printColor = printColorMatch ? printColorMatch[1] : null;
      // Нормализуем цвет принта (gray -> grey)
      const normalizedPrintColor = printColor === 'gray' ? 'grey' : printColor;
      
      if (normalizedPrintColor) {
        // Ищем изображения, где первый цвет = selectedBaseColor, второй цвет = printColor
        const matchingImages = nonPreviewImages.filter(img => {
          const altText = (img.altText || '').toLowerCase().trim();
          
          // Находим позиции цветов в alt text (учитываем оба варианта написания)
          const baseColorIndex = altText.indexOf(normalizedColor);
          const printColorIndex = altText.indexOf(normalizedPrintColor);
          
          // Основной цвет должен быть первым, принт - вторым
          if (baseColorIndex === -1 || printColorIndex === -1) {
            return false;
          }
          
          // Проверяем порядок: основной цвет должен идти перед цветом принта
          return baseColorIndex < printColorIndex;
        });
        
        if (matchingImages.length > 0) {
          return matchingImages;
        }
      }
      
      // Fallback: ищем по обоим словам
      const matchingImages = nonPreviewImages.filter(img => {
        const altText = (img.altText || '').toLowerCase();
        const normalizedAlt = altText.replace(/gray/g, 'grey');
        return normalizedAlt.includes(normalizedColor) && 
               (normalizedAlt.includes(selectedPrint.toLowerCase()) || 
                normalizedAlt.includes(normalizedPrintColor || ''));
      });
      
      if (matchingImages.length > 0) {
        return matchingImages;
      }
      
      // Если по alt text не нашли — используем изображение из варианта
      return getVariantImage();
    }
    
    // Если выбран только цвет (без принта), показываем все изображения этого цвета
    if (selectedBaseColor) {
      const colorLower = selectedBaseColor.toLowerCase();
      // Нормализуем для поиска (Gray -> grey)
      const normalizedColor = colorLower === 'gray' ? 'grey' : colorLower;
      
      const colorFilteredImages = nonPreviewImages.filter(img => {
        const altText = (img.altText || '').toLowerCase().trim();
        // Нормализуем alt text (gray -> grey)
        const normalizedAlt = altText.replace(/gray/g, 'grey');
        
        // Находим позицию основного цвета
        const baseColorIndex = normalizedAlt.indexOf(normalizedColor);
        if (baseColorIndex === -1) {
          return false;
        }
        
        // Проверяем, что это первый цвет в alt text (не цвет принта)
        // Ищем все цвета до позиции основного цвета
        const textBeforeColor = normalizedAlt.substring(0, baseColorIndex);
        const hasColorBefore = colorWords.some(color => textBeforeColor.includes(color));
        
        // Если нет других цветов перед основным, значит это основной цвет
        return !hasColorBefore;
      });
      
      if (colorFilteredImages.length > 0) {
        return colorFilteredImages;
      }
      
      // Если по alt text не нашли — используем изображение из варианта
      return getVariantImage();
    }
    
    // Если выбран вариант, используем его изображение
    if (selectedVariant) {
      return getVariantImage();
    }
    
    // Если ничего не выбрано, показываем первое изображение
    return nonPreviewImages.length > 0 ? [nonPreviewImages[0]] : product.images.slice(0, 1);
  }, [product, selectedBaseColor, selectedPrint, selectedVariant]);

  const getVariantByOptions = (selectedOptions: { [key: string]: string }) => {
    if (!product) return null;
    
    return product.variants.find(variant => 
      Object.keys(selectedOptions).every(optionName => 
        variant.selectedOptions.some(opt => 
          opt.name === optionName && opt.value === selectedOptions[optionName]
        )
      )
    );
  };

  // Handle color selection - find variant with selected color/combination and current size
  const handleColorSelect = (colorOrCombination: string) => {
    if (!product || !selectedVariant) return;
    
    // Get current size if selected
    const currentSize = selectedVariant.selectedOptions.find(opt => opt.name === 'Size')?.value;
    
    // Check if we have Color option or need to match by combination
    const hasColorOption = product.variants.some(v => 
      v.selectedOptions.some(opt => opt.name === 'Color')
    );
    
    let targetVariant: ShopifyVariant | undefined;
    
    if (hasColorOption) {
      // Если есть опция Color, ищем по ней
      targetVariant = product.variants.find(v => {
        const hasColor = v.selectedOptions.some(opt => 
          opt.name === 'Color' && opt.value === colorOrCombination
        );
        if (currentSize) {
          const hasSize = v.selectedOptions.some(opt => opt.name === 'Size' && opt.value === currentSize);
          return hasColor && hasSize && v.availableForSale;
        }
        return hasColor && v.availableForSale;
      });
      
      // If no variant with current size, find any available variant with this color
      if (!targetVariant) {
        targetVariant = product.variants.find(v => 
          v.selectedOptions.some(opt => opt.name === 'Color' && opt.value === colorOrCombination) &&
          v.availableForSale
        );
      }
    } else {
      // Если нет опции Color, ищем по комбинации опций
      targetVariant = product.variants.find(v => {
        const variantOptions = v.selectedOptions
          .filter(opt => opt.name !== 'Size')
          .map(opt => opt.value)
          .sort()
          .join(' + ');
        
        const matchesCombination = variantOptions === colorOrCombination || v.title === colorOrCombination;
        
        if (currentSize) {
          const hasSize = v.selectedOptions.some(opt => opt.name === 'Size' && opt.value === currentSize);
          return matchesCombination && hasSize && v.availableForSale;
        }
        return matchesCombination && v.availableForSale;
      });
      
      // If no variant with current size, find any available variant with this combination
      if (!targetVariant) {
        targetVariant = product.variants.find(v => {
          const variantOptions = v.selectedOptions
            .filter(opt => opt.name !== 'Size')
            .map(opt => opt.value)
            .sort()
            .join(' + ');
          return (variantOptions === colorOrCombination || v.title === colorOrCombination) && v.availableForSale;
        });
      }
    }
    
    if (targetVariant) {
      handleVariantChange(targetVariant);
    }
  };

  if (loading) {
    return (
      <div className="product-page">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-page">
        <div className="error-page">
          <h1>Product Not Found</h1>
          <p>The product you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-page">
      <div className="product-page__container">
        {/* Left: Product Description */}
        <div className="product-page__description-section">
          <div className="product-page__title-container">
            {getBrand() && (
              <h1 className="product-page__brand">
                {getBrand()}
              </h1>
            )}
            <h2 className="product-page__name">
              {getProductName()}
            </h2>
          </div>

          {product.description && (
            <div className="product-page__description">
              <div 
                className="product-page__description-text"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>
          )}

          {/* Product Details */}
          <div className="product-page__details">
            {product.tags.length > 0 && (
              <div className="product-page__detail-item">
                <p className="product-page__detail-text">
                  {product.tags.filter(tag => 
                    !tag.toLowerCase().includes('brand') && 
                    !tag.toLowerCase().includes('designer') &&
                    !tag.toLowerCase().includes('vendor')
                  ).join(' · ')}
                </p>
              </div>
            )}
            {product.id && (
              <p className="product-page__sku">
                {product.id.split('/').pop()}
              </p>
            )}
          </div>
        </div>

        {/* Center: Product Images with Vertical Scroll */}
        <div className="product-page__images">
          <div className="product-page__images-scroll">
            {colorImages.length > 0 ? (
              colorImages.map((image, index) => (
                <div
                  key={image.id}
                  className="product-page__image-wrapper"
                >
                  <img
                    src={image.url}
                    alt={image.altText || `${product.title} ${index + 1}`}
                    className="product-page__image"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              ))
            ) : (
              <div className="product-page__no-image">No Image Available</div>
            )}
          </div>
        </div>

        {/* Right: Variant Selection */}
        <div className="product-page__info">
          {/* Price */}
          {selectedVariant && (
            <div className="product-page__price-container">
              <div className="product-page__price">
                {formatPrice(selectedVariant.price.amount, selectedVariant.price.currencyCode)}
              </div>
            </div>
          )}

          {/* Variant Selection */}
          <div className="product-page__variants">
            {/* Base Color Selection */}
            {(() => {
              if (baseColors.length === 0) return null;
              
              return (
                <div className="product-page__option">
                  <h3 className="product-page__option-title">Color</h3>
                  <div className="product-page__option-values">
                    {baseColors.map((color) => {
                      // Проверяем, есть ли хотя бы один вариант с этим цветом (независимо от доступности)
                      const hasVariant = product.variants.some(v => {
                        const variantBaseColor = extractBaseColor(v);
                        return variantBaseColor?.toLowerCase() === color.toLowerCase();
                      });
                      
                      // Проверяем, есть ли доступные варианты
                      const hasAvailableVariant = product.variants.some(v => {
                        const variantBaseColor = extractBaseColor(v);
                        return variantBaseColor?.toLowerCase() === color.toLowerCase() && v.availableForSale;
                      });
                      
                      const isSelected = selectedBaseColor?.toLowerCase() === color.toLowerCase();

                      return (
                        <button
                          key={color}
                          onClick={() => handleBaseColorSelect(color)}
                          disabled={!hasVariant}
                          className={`product-page__option-value ${
                            isSelected ? 'product-page__option-value--selected' : ''
                          } ${
                            !hasAvailableVariant && hasVariant ? 'product-page__option-value--sold-out' : ''
                          } ${
                            !hasVariant ? 'product-page__option-value--disabled' : ''
                          }`}
                          title={!hasAvailableVariant && hasVariant ? 'Sold Out' : ''}
                        >
                          {color}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Print Selection (only if base color is selected) */}
            {selectedBaseColor && (() => {
              const prints = getPrintsForColor(selectedBaseColor);
              if (prints.length === 0) return null;
              
              return (
                <div className="product-page__option">
                  <h3 className="product-page__option-title">Print</h3>
                  <div className="product-page__option-values">
                    {prints.map((print) => {
                      // Проверяем, есть ли хотя бы один вариант с этим принтом (независимо от доступности)
                      const hasVariant = product.variants.some(v => {
                        const variantBaseColor = extractBaseColor(v);
                        const variantPrint = extractPrint(v);
                        return variantBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase() &&
                               variantPrint?.toLowerCase() === print.toLowerCase();
                      });
                      
                      // Проверяем, есть ли доступные варианты
                      const hasAvailableVariant = product.variants.some(v => {
                        const variantBaseColor = extractBaseColor(v);
                        const variantPrint = extractPrint(v);
                        return variantBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase() &&
                               variantPrint?.toLowerCase() === print.toLowerCase() &&
                               v.availableForSale;
                      });
                      
                      const isSelected = selectedPrint?.toLowerCase() === print.toLowerCase();

                      return (
                        <button
                          key={print}
                          onClick={() => handlePrintSelect(print)}
                          disabled={!hasVariant}
                          className={`product-page__option-value ${
                            isSelected ? 'product-page__option-value--selected' : ''
                          } ${
                            !hasAvailableVariant && hasVariant ? 'product-page__option-value--sold-out' : ''
                          } ${
                            !hasVariant ? 'product-page__option-value--disabled' : ''
                          }`}
                          title={!hasAvailableVariant && hasVariant ? 'Sold Out' : ''}
                        >
                          {print}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Size Selection */}
            {getUniqueOptions('Size').length > 0 && (
              <div className="product-page__option">
                <h3 className="product-page__option-title">Size</h3>
                <div className="product-page__option-values">
                  {getUniqueOptions('Size').map((size) => {
                    // Find variant with selected color, print, and this size
                    let variant: ShopifyVariant | undefined = undefined;
                    let variantUnavailable: ShopifyVariant | undefined = undefined;
                    
                    if (selectedBaseColor) {
                      // Сначала ищем доступный вариант
                      variant = product.variants.find(v => {
                        const variantBaseColor = extractBaseColor(v);
                        const variantPrint = extractPrint(v);
                        const hasSize = v.selectedOptions.some(opt => opt.name === 'Size' && opt.value === size);
                        
                        const colorMatch = variantBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase();
                        const printMatch = !selectedPrint || variantPrint?.toLowerCase() === selectedPrint.toLowerCase();
                        
                        return colorMatch && printMatch && hasSize && v.availableForSale;
                      });
                      
                      // Если не нашли доступный, ищем любой вариант с этим размером
                      if (!variant) {
                        variantUnavailable = product.variants.find(v => {
                          const variantBaseColor = extractBaseColor(v);
                          const variantPrint = extractPrint(v);
                          const hasSize = v.selectedOptions.some(opt => opt.name === 'Size' && opt.value === size);
                          
                          const colorMatch = variantBaseColor?.toLowerCase() === selectedBaseColor.toLowerCase();
                          const printMatch = !selectedPrint || variantPrint?.toLowerCase() === selectedPrint.toLowerCase();
                          
                          return colorMatch && printMatch && hasSize;
                        });
                      }
                    }
                    
                    const isSelected = selectedVariant?.selectedOptions.some(
                      opt => opt.name === 'Size' && opt.value === size
                    );
                    const isAvailable = variant?.availableForSale || false;
                    const hasVariant = variant || variantUnavailable;

                    return (
                      <button
                        key={size}
                        onClick={() => (variant || variantUnavailable) && handleVariantChange(variant || variantUnavailable!)}
                        disabled={!hasVariant}
                        className={`product-page__option-value ${
                          isSelected ? 'product-page__option-value--selected' : ''
                        } ${
                          !isAvailable && hasVariant ? 'product-page__option-value--sold-out' : ''
                        } ${
                          !hasVariant ? 'product-page__option-value--disabled' : ''
                        }`}
                        title={!isAvailable && hasVariant ? 'Sold Out' : ''}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Add to Cart */}
          <div className="product-page__actions">
            <button
              onClick={handleAddToCart}
              disabled={!selectedVariant?.availableForSale || isAddingToCart}
              className="product-page__add-to-cart"
            >
              {isAddingToCart 
                ? 'Adding...' 
                : selectedVariant?.availableForSale 
                  ? 'Add to Cart' 
                  : 'Sold Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;